import React, { useEffect, useState } from "react";
import { Bar } from "react-chartjs-2";
import { ChartData, ChartOptions } from "chart.js";
import { CubeSummaryResponse } from "../schemas/kpi.types";
import { kpiService } from "./kpi-service";
import { ReactComponent as IconProveedorAmbulatorio } from "../../../assets/img/icon/dashboard/proveedor_ambulatorio.svg";
import { ReactComponent as IconProveedorHospitalario } from "../../../assets/img/icon/dashboard/proveedor_hospitalario.svg";
import { ReactComponent as IconMenu } from "../../../assets/img/icon/dashboard/dashboard_button_modal.svg";
import { StatsModal } from "./StatsModal";
import { ProdesegButton, ProdesegButtonEnumTypes } from "../../../components/ProdesegButton/ProdesegButton";
import { Spinner } from "reactstrap";
import { formatCurrency, formatPercent } from "../schemas/dashboard.utils";
import { useBoolean } from "../../../custom-hooks/useBoolean";
import { CubeBasicFilters } from "../schemas/kpi.types";

export const KPIBarGraph: React.FC<{
    cubeFilters: CubeBasicFilters;
    typeAssistance: string;
    titleChart: string;
    groupBy: boolean;
}> = ({ cubeFilters, typeAssistance, titleChart, groupBy }) => {
    const [chartData, setChartData] = useState<ChartData<"bar", number[]>>({
        labels: [],
        datasets: [],
    });

    const [rawStats, setRawStats] = useState<CubeSummaryResponse[]>([]);
    const { value: isModalOpen, toggle: toggleModal } = useBoolean(false);
    const [displayAmounts, setDisplayAmounts] = useState<number[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const { idClient, idQuotes, policy } = cubeFilters;

    useEffect(() => {
        const loadSupplierInformation = async () => {
            try {
                setLoading(true);
                const barGraphData: CubeSummaryResponse[] = groupBy
                    ? await kpiService.fetchSupplierSummary(cubeFilters)
                    : await kpiService.fetchSupplier(cubeFilters, typeAssistance);

                setRawStats(barGraphData);
                setDisplayAmounts(barGraphData.map(graph => graph.amount));

                const total = barGraphData.reduce((acc, curr) => acc + curr.amount, 0);

                setChartData({
                    labels: barGraphData.map(graph => graph.type),
                    datasets: [
                        {
                            label: "Siniestros",
                            data: barGraphData.map(graph => (total > 0 ? (graph.amount / total) * 100 : 0)),
                            backgroundColor: ["#087678", "#189B9C", "#4DBDBF", "#A6DEDF", "#E9E572"],
                            borderRadius: 16,
                            borderSkipped: false,
                            barThickness: 120,
                        },
                    ],
                });
            } catch (error) {
                console.error("Error:", error);
            } finally {
                setLoading(false);
            }
        };

        if (idClient) loadSupplierInformation();
    }, [idClient, idQuotes, policy, typeAssistance]);

    const options: ChartOptions<"bar"> = {
        responsive: true,
        maintainAspectRatio: false,
        layout: {
            padding: {
                top: 40,
                bottom: 5,
            },
        },
        plugins: {
            legend: {
                display: false,
            },
            tooltip: {
                callbacks: {
                    label: context => {
                        const value = context.parsed.y || 0;
                        return `Total: ${formatPercent(value)}`;
                    },
                },
            },
            datalabels: {
                display: true,
                anchor: "end",
                align: "top",
                offset: 8,
                formatter: (value: number) => {
                    return value.toFixed(1) + "%";
                },
                color: "#148F8B",
                font: {
                    weight: "bold",
                    size: 14,
                },
            },
        },
        scales: {
            x: {
                grid: {
                    display: false,
                },
                ticks: {
                    callback: (_value, index) => {
                        const label = chartData.labels?.[index] ?? "";
                        const barName = String(label);

                        return barName.length > 20 ? barName.substring(0, 17) + "..." : barName;
                    },
                },
            },
            y: {
                grid: {
                    display: false,
                },
                display: false,
            },
        },
    };

    return (
        <div className="siniestralidad-container">
            {loading && (
                <div className="text-center py-4">
                    <Spinner color="primary" />
                </div>
            )}
            <div className="header-section-doughnut">
                <div className="header-title-group">
                    <div className="icon-wrapper">
                        {typeAssistance === "Ambulatorio" ? (
                            <IconProveedorAmbulatorio />
                        ) : (
                            <IconProveedorHospitalario />
                        )}
                    </div>
                    <h2 className="title-label-doughnut">{titleChart}</h2>
                </div>

                <ProdesegButton
                    type={ProdesegButtonEnumTypes.PrimaryLight}
                    Icon={IconMenu}
                    onlyIcon
                    isOutline
                    onClick={toggleModal}
                    aria-label="Abrir detalle"
                />
            </div>

            <div className="kpi-chart-wrapper">
                <Bar data={chartData} options={options} />
            </div>

            <div className="kpi-footer-values">
                {displayAmounts.map((amt, i) => (
                    <div key={`${i} - ${amt}`} className="kpi-value-item">
                        {formatCurrency(amt)}
                    </div>
                ))}
            </div>

            <StatsModal<CubeSummaryResponse>
                isOpen={isModalOpen}
                toggle={toggleModal}
                title={titleChart}
                data={rawStats}
                labelField="type"
                valueField="amount"
                valueCountField={"total"}
                queryType={typeAssistance}
            />
        </div>
    );
};
